import {type PropsWithChildren, useEffect, useLayoutEffect, useRef} from "react";
import {BackNavigationGuard} from "@/app/BackNavigationGuard";
import {PwaInstallPrompt} from "@/components";
import {Outlet, useLocation, useNavigationType} from "react-router-dom";
import {getActiveBottomSheetCloseHandler} from "./bottomSheetRegistry";

const PAGE_SHELL_SCROLL_STATE_KEY = "__pageShellScrollPosition";
const PULL_TO_ACTION_DISTANCE_PX = 112;
const MAX_PULL_ANGLE_DRIFT_PX = 48;
const PULL_TO_REFRESH_DISABLED_PATHS = new Set(["/auth/sign-in", "/auth/sign-up"]);

interface PageShellHistoryState {
  __pageShellScrollPosition?: {
    top: number;
    left: number;
  };
}

interface PullGestureState {
  activeContainer: HTMLElement | null;
  isEligible: boolean;
  maxPullDistance: number;
  startX: number;
  startY: number;
}

const isVerticallyScrollable = (element: HTMLElement) => {
  const {overflowY} = window.getComputedStyle(element);

  return /(auto|scroll|overlay)/.test(overflowY) && element.scrollHeight > element.clientHeight;
};

const getPageShellElement = () => document.querySelector<HTMLElement>(".app-page-shell");

const getClosestScrollableAncestor = (target: HTMLElement, boundary: HTMLElement) => {
  for (
    let currentElement: HTMLElement | null = target;
    currentElement && currentElement !== boundary;
    currentElement = currentElement.parentElement
  ) {
    if (isVerticallyScrollable(currentElement)) {
      return currentElement;
    }
  }

  return boundary;
};

const getGestureContainer = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return (
      document.querySelector<HTMLElement>("[data-bottom-sheet-scroll]") ?? getPageShellElement()
    );
  }

  const bottomSheetScroll = target.closest<HTMLElement>("[data-bottom-sheet-scroll]");

  if (bottomSheetScroll) {
    return getClosestScrollableAncestor(target, bottomSheetScroll);
  }

  const pageShell = getPageShellElement();

  if (pageShell?.contains(target)) {
    return getClosestScrollableAncestor(target, pageShell);
  }

  return pageShell;
};

export const PageShell = ({children}: PropsWithChildren) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const containerRef = useRef<HTMLElement | null>(null);
  const pullGestureRef = useRef<PullGestureState>({
    activeContainer: null,
    isEligible: false,
    maxPullDistance: 0,
    startX: 0,
    startY: 0,
  });

  useLayoutEffect(() => {
    const locationKey = location.key;

    if (navigationType !== "POP") {
      return;
    }

    const scrollPosition = (window.history.state as PageShellHistoryState | null)?.[
      PAGE_SHELL_SCROLL_STATE_KEY
    ];

    if (!scrollPosition || !containerRef.current) {
      return;
    }

    const restoreScrollPosition = () => {
      if (!containerRef.current) {
        return;
      }

      if (!locationKey) {
        return;
      }

      containerRef.current.scrollTop = scrollPosition.top;
      containerRef.current.scrollLeft = scrollPosition.left;
    };

    restoreScrollPosition();
    requestAnimationFrame(restoreScrollPosition);
  }, [location, navigationType]);

  useEffect(() => {
    const resetPullGesture = () => {
      pullGestureRef.current = {
        activeContainer: null,
        isEligible: false,
        maxPullDistance: 0,
        startX: 0,
        startY: 0,
      };
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        resetPullGesture();
        return;
      }

      const touch = event.touches.item(0);

      if (!touch) {
        resetPullGesture();
        return;
      }

      const activeContainer = getGestureContainer(event.target);

      pullGestureRef.current = {
        activeContainer,
        isEligible: (activeContainer?.scrollTop ?? 0) <= 0,
        maxPullDistance: 0,
        startX: touch.clientX,
        startY: touch.clientY,
      };
    };

    const handleTouchMove = (event: TouchEvent) => {
      const gestureState = pullGestureRef.current;

      if (!gestureState.activeContainer || !gestureState.isEligible || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches.item(0);

      if (!touch) {
        return;
      }

      const verticalDelta = touch.clientY - gestureState.startY;
      const horizontalDrift = Math.abs(touch.clientX - gestureState.startX);

      if (verticalDelta <= 0 || horizontalDrift > MAX_PULL_ANGLE_DRIFT_PX) {
        return;
      }

      if (gestureState.activeContainer.scrollTop > 0) {
        return;
      }

      gestureState.maxPullDistance = Math.max(gestureState.maxPullDistance, verticalDelta);

      if (gestureState.maxPullDistance > 0) {
        event.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      const {maxPullDistance} = pullGestureRef.current;

      if (maxPullDistance < PULL_TO_ACTION_DISTANCE_PX) {
        resetPullGesture();
        return;
      }

      const closeBottomSheet = getActiveBottomSheetCloseHandler();

      resetPullGesture();

      if (closeBottomSheet) {
        closeBottomSheet();
        return;
      }

      if (PULL_TO_REFRESH_DISABLED_PATHS.has(location.pathname)) {
        return;
      }

      window.location.reload();
    };

    document.addEventListener("touchstart", handleTouchStart, {passive: true});
    document.addEventListener("touchmove", handleTouchMove, {passive: false});
    document.addEventListener("touchend", handleTouchEnd, {passive: true});
    document.addEventListener("touchcancel", resetPullGesture, {passive: true});

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", resetPullGesture);
    };
  }, [location.pathname]);

  return (
    <section
      ref={containerRef}
      className="mx-auto w-full px-6 h-screen overflow-y-scroll app-page-shell pb-[calc(env(safe-area-inset-bottom)+2rem)] no-scrollbar overscroll-none"
    >
      <BackNavigationGuard />
      <PwaInstallPrompt />
      {children || <Outlet />}
    </section>
  );
};
